import { z } from 'zod';
import { EMPLOYMENT_TYPES } from '../enums.js';
import { SPECIAL_RUN_COMPONENTS } from '../payroll/calc.js';
import { INELIGIBLE_REASONS } from '../payroll/eligibility.js';
import { BANK_ADVICE_FORMATS, BANK_UPLOAD_FORMATS, DISBURSEMENT_CHANNELS, PAY_INPUT_SOURCES, PAY_ITEM_SOURCES, PAY_PARTS, PAY_RUN_INPUTS, PAY_RUN_STATUSES, PAY_RUN_TYPES, } from '../payroll/enums.js';
import { PAYMENT_MODES } from '../enums.js';
import { isoDate, money, optionalText, paginated, paginationQuerySchema, uuid } from './common.js';
/** LWP days: 0 or more in steps of 0.5 (BR-PAY-3); ≤ employed days is checked by the engine. */
export const lwpDays = z.number().min(0).max(31).multipleOf(0.5);
/** Special-run percentage of gross: 0 < % ≤ 200, up to 2 decimals (SRS §17). */
export const specialRunPercent = z
    .number()
    .gt(0)
    .max(200)
    .refine((v) => Math.abs(v * 100 - Math.round(v * 100)) < 1e-6, 'Use at most 2 decimals');
/** Statuses on the cutoff date a special run may include (FR-PAY-13). */
export const SPECIAL_RUN_STATUSES = ['ACTIVE', 'NOTICE_PERIOD'];
/** `runs.config` of a BONUS or LTA run (FR-PAY-13, ADR-052). */
export const specialRunConfigSchema = z
    .object({
    component: z.enum(SPECIAL_RUN_COMPONENTS),
    percent: specialRunPercent,
    cutoffDate: isoDate,
    criteria: z
        .object({
        employmentTypes: z
            .array(z.enum(EMPLOYMENT_TYPES))
            .min(1)
            .refine((v) => new Set(v).size === v.length, 'Each employment type only once')
            .default(['PERMANENT']),
        confirmedOnOrBefore: isoDate.nullish(),
        joinedOnOrBefore: isoDate.nullish(),
        statuses: z
            .array(z.enum(SPECIAL_RUN_STATUSES))
            .min(1)
            .refine((v) => new Set(v).size === v.length, 'Each status only once')
            .default([...SPECIAL_RUN_STATUSES]),
    })
        .strict()
        .prefault({}),
})
    .strict();
/** MONTHLY runs carry no config. */
export const emptyRunConfigSchema = z.object({}).strict();
/** Most leavers one settlement run can pay. */
export const MAX_SETTLEMENT_EMPLOYEES = 200;
/** `runs.config` of a SETTLEMENT run: the leavers it pays (FR-PAY-14, ADR-070). */
export const settlementRunConfigSchema = z
    .object({
    employeeIds: z
        .array(uuid)
        .min(1, 'Choose at least one leaver')
        .max(MAX_SETTLEMENT_EMPLOYEES)
        .refine((v) => new Set(v).size === v.length, 'Each leaver only once'),
})
    .strict();
/**
 * `runs.config` per run type. A BONUS run pays a bonus component, an LTA run pays LTA.
 */
export const PAY_RUN_CONFIG_SCHEMAS = {
    MONTHLY: emptyRunConfigSchema,
    SETTLEMENT: settlementRunConfigSchema,
    BONUS: specialRunConfigSchema.refine((c) => c.component !== 'LTA', {
        path: ['component'],
        message: 'A bonus run pays a bonus component',
    }),
    LTA: specialRunConfigSchema.refine((c) => c.component === 'LTA', {
        path: ['component'],
        message: 'An LTA run pays LTA',
    }),
};
/** One per-employee run input (FR-PAY-2…4, FR-PAY-13); LWP days in 0.5 steps, money in taka. */
export const payRunInputSchema = z.discriminatedUnion('input', [
    z.object({ employeeId: uuid, input: z.literal('LWP_DAYS'), value: lwpDays }).strict(),
    z
        .object({
        employeeId: uuid,
        input: z.enum(PAY_RUN_INPUTS.filter((i) => i !== 'LWP_DAYS')),
        value: money,
    })
        .strict(),
]);
/** `PUT /payroll/runs/:id/inputs` — batch upsert, each employee/input at most once. */
export const payRunInputsUpsertSchema = z
    .object({ items: z.array(payRunInputSchema).min(1).max(5000) })
    .strict()
    .superRefine((value, ctx) => {
    const seen = new Set();
    value.items.forEach((item, i) => {
        const key = `${item.employeeId}:${item.input}`;
        if (seen.has(key)) {
            ctx.addIssue({
                code: 'custom',
                path: ['items', i, 'input'],
                message: 'Each employee input can appear only once',
            });
        }
        seen.add(key);
    });
});
/**
 * Result of `PUT /payroll/runs/:id/inputs`: values changed. Just a count, because the VAT & Tax
 * officer may enter AIT without being allowed to read runs (AC-8 c).
 */
export const payRunInputsResultSchema = z.object({ updated: z.number().int().min(0) });
// ── Approver chain (FR-PAY-11, ADR-052) ──────────────────────────────────────────────────────
/** Most approvers a payroll run can have (FR-PAY-11). */
export const MAX_PAYROLL_APPROVERS = 3;
/** A user who may be placed in the chain: an active account holding `payroll.approve`. */
export const payrollApproverCandidateSchema = z.object({
    userId: uuid,
    name: z.string(),
    empCode: z.number().int().nullable(),
});
export const payrollApproverSchema = payrollApproverCandidateSchema.extend({
    stepOrder: z.number().int().min(1).max(MAX_PAYROLL_APPROVERS),
    /** False when the user no longer holds `payroll.approve` or is inactive (fix the chain). */
    eligible: z.boolean(),
});
/** `GET /payroll/approver-chain`: the chain in order and who could be added. */
export const approverChainSchema = z.object({
    approvers: z.array(payrollApproverSchema),
    candidates: z.array(payrollApproverCandidateSchema),
});
/**
 * `PUT /payroll/approver-chain` — the whole chain: 1–3 distinct users with orders 1..n, each
 * order once (SRS §17). Holding `payroll.approve` is checked by the API.
 */
export const approverChainUpdateSchema = z
    .object({
    approvers: z
        .array(z
        .object({
        userId: uuid,
        stepOrder: z.number().int().min(1).max(MAX_PAYROLL_APPROVERS),
    })
        .strict())
        .min(1, 'Choose at least one approver')
        .max(MAX_PAYROLL_APPROVERS, `At most ${String(MAX_PAYROLL_APPROVERS)} approvers`),
})
    .strict()
    .superRefine((value, ctx) => {
    const users = new Set();
    const orders = new Set();
    value.approvers.forEach((a, i) => {
        if (users.has(a.userId)) {
            ctx.addIssue({
                code: 'custom',
                path: ['approvers', i, 'userId'],
                message: 'Each approver can appear only once',
            });
        }
        if (orders.has(a.stepOrder)) {
            ctx.addIssue({
                code: 'custom',
                path: ['approvers', i, 'stepOrder'],
                message: 'Each step order can be used only once',
            });
        }
        users.add(a.userId);
        orders.add(a.stepOrder);
    });
    if (orders.size === value.approvers.length) {
        const missing = value.approvers.findIndex((_a, i) => !orders.has(i + 1));
        if (missing !== -1) {
            ctx.addIssue({
                code: 'custom',
                path: ['approvers'],
                message: `Step orders must run 1 to ${String(value.approvers.length)} without gaps`,
            });
        }
    }
});
// ── Standing amounts (FR-PAY-2/3, ADR-053) ───────────────────────────────────────────────────
/** Standing monthly amounts: Food (`payroll.prepare`) and AIT (`payroll.tax.manage`). */
export const STANDING_KINDS = ['food', 'ait'];
/** The pay component each standing kind is stored under. */
export const STANDING_COMPONENT_CODES = {
    food: 'FOOD',
    ait: 'AIT',
};
export const standingKindParamSchema = z.object({ kind: z.enum(STANDING_KINDS) });
/** `GET /payroll/standing/:kind` — current employees, search by name or code. */
export const standingListQuerySchema = paginationQuerySchema.extend({
    q: z.string().trim().max(100).optional(),
});
/**
 * One employee's standing amount. AIT rows also carry the current gross (OQ-16 — the tax officer
 * sees name, code, gross and AIT only); Food rows never do.
 */
export const standingRowSchema = z.object({
    employeeId: uuid,
    empCode: z.number().int(),
    fullName: z.string(),
    /** Amount in effect today (`null` = never set). */
    amount: z.number().int().nullable(),
    effectiveDate: isoDate.nullable(),
    /** A later change already recorded, if any. */
    scheduled: z.object({ amount: z.number().int(), effectiveDate: isoDate }).nullable(),
    /** AIT only: current gross salary. */
    gross: z.number().int().nullable().optional(),
});
export const standingListResponseSchema = paginated(standingRowSchema);
/**
 * `PUT /payroll/standing/:kind` — bulk entry: amounts per employee from one effective date.
 * 0 means "none" (not opted in / no AIT). A row on the same date is replaced.
 */
export const standingUpdateSchema = z
    .object({
    effectiveDate: isoDate,
    items: z
        .array(z.object({ employeeId: uuid, amount: money }).strict())
        .min(1)
        .max(1000),
})
    .strict()
    .superRefine((value, ctx) => {
    const seen = new Set();
    value.items.forEach((item, i) => {
        if (seen.has(item.employeeId)) {
            ctx.addIssue({
                code: 'custom',
                path: ['items', i, 'employeeId'],
                message: 'Each employee can appear only once',
            });
        }
        seen.add(item.employeeId);
    });
});
export const standingUpdateResultSchema = z.object({
    /** Rows written (unchanged amounts on the same date are skipped). */
    updated: z.number().int().min(0),
});
// ── Runs (FR-PAY-5…10, FR-PAY-16, AC-8) ──────────────────────────────────────────────────────
/**
 * Line flags: the engine's (`DEDUCTION_SPILLOVER`, `NEGATIVE_NET`) and the run checks
 * (FR-PAY-16, ADR-064). `BLOCKING_LINE_FLAGS` stop submission (T-067); `ZERO_SALARY_LINE_FLAGS`
 * are warnings — such lines are left out of the run when HR confirms the submission (FR-PAY-23,
 * ADR-088).
 */
export const RUN_LINE_FLAGS = [
    'DEDUCTION_SPILLOVER',
    'NEGATIVE_NET',
    'ZERO_NET',
    'NO_BANK_ACCOUNT',
    'NEW_JOINER',
    'LWP_CAPPED',
    'NO_SALARY',
    'AMOUNT_OVERRIDDEN',
    'PREVIOUS_MONTH_UNPAID',
    'NO_EXIT',
    'IN_MONTHLY_RUN',
];
export const BLOCKING_LINE_FLAGS = [
    'NEGATIVE_NET',
    'NO_BANK_ACCOUNT',
    'NO_EXIT',
    'IN_MONTHLY_RUN',
];
/** Zero-salary lines: a warning; left out on a confirmed submission (FR-PAY-23, ADR-088). */
export const ZERO_SALARY_LINE_FLAGS = ['NO_SALARY', 'ZERO_NET'];
/** `exclude_reason` of lines left out on submission for zero salary (cleared on rejection). */
export const ZERO_SALARY_EXCLUDE_REASON = 'Zero salary';
/** `POST /payroll/runs/:id/submit` (FR-PAY-11, FR-PAY-23). */
export const payRunSubmitSchema = z
    .object({
    /** Leave the zero-salary lines out of the run (else 409 listing them). */
    confirmExcludeZeroLines: z.boolean().optional(),
})
    .strict();
/** Run names: 2–120 characters. */
const runName = z.string().trim().min(2).max(120);
/** `POST /payroll/runs` for a MONTHLY run: a calendar month (FR-PAY-5). */
export const payRunMonthlyCreateSchema = z
    .object({
    type: z.literal('MONTHLY'),
    year: z.number().int().min(2000).max(2100),
    month: z.number().int().min(1).max(12),
    payDate: isoDate,
    /** Default: "October 2026 payroll". */
    name: runName.optional(),
})
    .strict();
/**
 * `POST /payroll/runs` for a BONUS or LTA run (FR-PAY-13): a name, the pay date (its month is
 * the run's pay month) and the config — component, % of gross, cutoff and criteria.
 */
export const payRunBonusCreateSchema = z
    .object({
    type: z.literal('BONUS'),
    name: runName,
    payDate: isoDate,
    config: PAY_RUN_CONFIG_SCHEMAS.BONUS,
})
    .strict();
export const payRunLtaCreateSchema = z
    .object({
    type: z.literal('LTA'),
    name: runName,
    payDate: isoDate,
    config: PAY_RUN_CONFIG_SCHEMAS.LTA,
})
    .strict();
/** A special run's settings; also the body of the eligibility preview. */
export const payRunSpecialCreateSchema = z.discriminatedUnion('type', [
    payRunBonusCreateSchema,
    payRunLtaCreateSchema,
]);
/**
 * `POST /payroll/runs` for a SETTLEMENT run (FR-PAY-14): leavers paid from the 1st of their last
 * working day's month (or joining) to that day, plus manual additions / deductions.
 */
export const payRunSettlementCreateSchema = z
    .object({
    type: z.literal('SETTLEMENT'),
    name: runName,
    payDate: isoDate,
    config: settlementRunConfigSchema,
})
    .strict();
/** `POST /payroll/runs` — MONTHLY, BONUS, LTA or SETTLEMENT. */
export const payRunCreateSchema = z.discriminatedUnion('type', [
    payRunSettlementCreateSchema,
    payRunMonthlyCreateSchema,
    payRunBonusCreateSchema,
    payRunLtaCreateSchema,
]);
export const payRunListQuerySchema = paginationQuerySchema.extend({
    type: z.enum(PAY_RUN_TYPES).optional(),
    status: z.enum(PAY_RUN_STATUSES).optional(),
});
export const payRunTotalsSchema = z.object({
    employees: z.number().int(),
    earnings: z.number().int(),
    deductions: z.number().int(),
    net: z.number().int(),
    netCash: z.number().int(),
    netBank: z.number().int(),
});
export const payRunItemSchema = z.object({
    id: uuid,
    type: z.enum(PAY_RUN_TYPES),
    name: z.string(),
    period: z
        .object({ year: z.number().int(), month: z.number().int(), start: isoDate, end: isoDate })
        .nullable(),
    payDate: isoDate,
    status: z.enum(PAY_RUN_STATUSES),
    totals: payRunTotalsSchema,
    /** Lines with a blocking flag (submission is refused while > 0). */
    blockingLines: z.number().int(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const payRunListResponseSchema = paginated(payRunItemSchema);
const personRef = z.object({ employeeId: uuid, empCode: z.number().int(), fullName: z.string() });
/** `GET /payroll/runs/:id` — the run, totals and check summary (FR-PAY-6, FR-PAY-16). */
export const payRunDetailSchema = payRunItemSchema.extend({
    /** Leavers whose last working day is inside the period: paid by a settlement run (FR-PAY-6). */
    needsSettlement: z.array(personRef.extend({ lastWorkingDay: isoDate })),
    /** Lines per flag. */
    flagCounts: z.record(z.string(), z.number().int()),
    /** The previous monthly run used for variance, if any. */
    previousRun: z.object({ id: uuid, name: z.string() }).nullable(),
    /** Which inputs the caller may edit (ADR-088): AIT never on their own line (BR-PAY-2). */
    canEdit: z.object({ inputs: z.boolean(), ait: z.boolean() }),
    /** The PAYROLL_RUN approval request of the latest submission (ADR-066), if any. */
    approvalRequestId: uuid.nullable(),
    submittedAt: z.string().nullable(),
    approvedAt: z.string().nullable(),
    /** The caller may submit now: draft, payroll.prepare, nothing blocking, a chain set up. */
    canSubmit: z.boolean(),
    /** Why the caller cannot submit (shown next to the hidden / disabled action), if relevant. */
    submitBlockedReason: z.string().nullable(),
    /**
     * Included lines flagged `NO_SALARY` / `ZERO_NET`: left out of the run if HR submits it anyway
     * (FR-PAY-23, ADR-088). The flag is the first zero-salary flag of the line.
     */
    zeroSalaryLines: z.array(personRef.extend({ flag: z.enum(RUN_LINE_FLAGS) })),
    /**
     * Disbursement per channel with something to pay (FR-PAY-17, ADR-068): amount (run net cash /
     * net bank), last file generated and whether it was paid. Only for approved / disbursed runs.
     */
    disbursements: z.array(z.object({
        channel: z.enum(DISBURSEMENT_CHANNELS),
        amount: z.number().int(),
        generatedAt: z.string().nullable(),
        disbursedOn: isoDate.nullable(),
        reference: z.string().nullable(),
    })),
    /** The caller may download the advice / cash sheet and mark parts paid (payroll.disburse). */
    canDisburse: z.boolean(),
    /**
     * Bank advice files the caller may download (payroll.disburse; approved / disbursed runs with
     * a bank part, else empty), and why an upload file is not available (ADR-073).
     */
    bankAdviceFormats: z.array(z.object({
        format: z.enum(BANK_ADVICE_FORMATS),
        label: z.string(),
        available: z.boolean(),
        reason: z.string().nullable(),
    })),
    /** BONUS / LTA runs: component, % of gross, cutoff and criteria (FR-PAY-13); else null. */
    special: specialRunConfigSchema.nullable(),
    /** Lines left out (HR's bonus / LTA exclusions, zero salary); not counted in the totals. */
    excludedLines: z.number().int(),
});
export const payRunLineItemSchema = z.object({
    code: z.string(),
    name: z.string(),
    kind: z.enum(['EARNING', 'DEDUCTION']),
    part: z.enum(PAY_PARTS),
    amount: z.number().int(),
    source: z.enum(PAY_ITEM_SOURCES),
});
/** A day behind an attendance count: 1, or 0.5 for a half day. */
const attendanceCountDaySchema = z.object({ date: isoDate, days: z.number() });
/**
 * Attendance counts shown beside an employee in a monthly / settlement run (FR-PAY-20,
 * FR-ATT-14, ADR-088) — for HR's information only; they never change pay. Absent and Late cover
 * the days already recorded; Unpaid leave covers approved unpaid leave anywhere in the period.
 */
export const payrollAttendanceCountsSchema = z.object({
    absentDays: z.number(),
    lateDays: z.number(),
    unpaidLeaveDays: z.number(),
    dates: z.object({
        absent: z.array(attendanceCountDaySchema),
        late: z.array(attendanceCountDaySchema),
        unpaidLeave: z.array(attendanceCountDaySchema),
    }),
});
/** One line of a run (AC-8: `payroll.read`, or `payroll.disburse` once approved). */
export const payRunLineSchema = personRef.extend({
    id: uuid,
    paymentMode: z.enum(PAYMENT_MODES).nullable(),
    gross: z.number().int(),
    calendarDays: z.number().int(),
    employedDays: z.number(),
    lwpDays: z.number(),
    payableDays: z.number(),
    earningsTotal: z.number().int(),
    deductionsTotal: z.number().int(),
    net: z.number().int(),
    netCash: z.number().int(),
    netBank: z.number().int(),
    flags: z.array(z.string()),
    items: z.array(payRunLineItemSchema),
    inputs: z.array(z.object({
        input: z.enum(PAY_RUN_INPUTS),
        value: z.number(),
        source: z.enum(PAY_INPUT_SOURCES),
        /** Why HR set it (special-run amount overrides, FR-PAY-13). */
        reason: z.string().nullable(),
        /** A provider's LWP breakdown — only on runs calculated before ADR-088 (FR-PAY-20). */
        details: z.array(z.object({ label: z.string(), days: z.number() })).optional(),
    })),
    /** Absent / late / unpaid-leave counts from the input provider; null without one (ADR-088). */
    attendanceCounts: payrollAttendanceCountsSchema.nullable(),
    /** Excluded by HR (special runs, FR-PAY-13): not paid, not in the totals. */
    excluded: z.boolean(),
    excludeReason: z.string().nullable(),
    /** The same employee's line in the previous monthly run (FR-PAY-16). */
    previous: z
        .object({ gross: z.number().int(), deductionsTotal: z.number().int(), net: z.number().int() })
        .nullable(),
    /** True for the caller's own line — editable except its AIT (ADR-088 (3), BR-PAY-2). */
    own: z.boolean(),
});
export const payRunLinesQuerySchema = paginationQuerySchema.extend({
    q: z.string().trim().max(100).optional(),
    flag: z.enum(RUN_LINE_FLAGS).optional(),
});
export const payRunLinesResponseSchema = paginated(payRunLineSchema);
// ── Special runs (FR-PAY-13, OQ-15) ──────────────────────────────────────────────────────────
/** Counts of people left out, per first failing criterion. */
const ineligibleCountsSchema = z.object(Object.fromEntries(INELIGIBLE_REASONS.map((r) => [r, z.number().int()])));
/**
 * `POST /payroll/runs/preview` — who a BONUS / LTA run would pay and how much (US-P6), before
 * creating it: eligible people with gross on the cutoff, amount and its cash / bank split.
 */
export const specialRunPreviewSchema = z.object({
    eligible: z.number().int(),
    ineligible: ineligibleCountsSchema,
    totals: z.object({
        amount: z.number().int(),
        netCash: z.number().int(),
        netBank: z.number().int(),
    }),
    items: z.array(personRef.extend({
        employmentType: z.enum(EMPLOYMENT_TYPES).nullable(),
        paymentMode: z.enum(PAYMENT_MODES).nullable(),
        gross: z.number().int(),
        amount: z.number().int(),
        netCash: z.number().int(),
        netBank: z.number().int(),
        flags: z.array(z.string()),
    })),
});
/** `PUT /payroll/runs/:id/adjustments/:employeeId` path. */
export const payRunAdjustParamSchema = z.object({ id: uuid, employeeId: uuid });
/**
 * `PUT /payroll/runs/:id/adjustments/:employeeId` — HR's decision on one eligible employee of a
 * special run (FR-PAY-13): pay the calculated amount (no reason), pay an override (> 0, reason)
 * or exclude (reason). To pay nothing, exclude.
 */
export const payRunLineAdjustSchema = z
    .object({
    excluded: z.boolean(),
    overrideAmount: money.min(1, 'To pay nothing, exclude the employee').nullable(),
    reason: optionalText(500),
})
    .strict()
    .superRefine((value, ctx) => {
    if (value.excluded && value.overrideAmount !== null) {
        ctx.addIssue({
            code: 'custom',
            path: ['overrideAmount'],
            message: 'An excluded employee has no amount',
        });
    }
    if ((value.excluded || value.overrideAmount !== null) && !value.reason) {
        ctx.addIssue({ code: 'custom', path: ['reason'], message: 'Give a reason' });
    }
});
// ── Payslips (FR-PAY-18, AC-8) ───────────────────────────────────────────────────────────────
/** One payslip of the caller (approved / disbursed runs only). */
export const payslipItemSchema = z.object({
    lineId: uuid,
    runId: uuid,
    runName: z.string(),
    runType: z.enum(PAY_RUN_TYPES),
    period: z.object({ year: z.number().int(), month: z.number().int() }).nullable(),
    payDate: isoDate,
    net: z.number().int(),
});
export const payslipListResponseSchema = paginated(payslipItemSchema);
export const payslipParamSchema = z.object({ lineId: uuid });
// ── Disbursement (FR-PAY-17, ADR-068) ────────────────────────────────────────────────────────
/** `POST /payroll/runs/:id/disburse` — one part (bank or cash) paid on a date. */
export const payRunDisburseSchema = z
    .object({
    channel: z.enum(DISBURSEMENT_CHANNELS),
    disbursedOn: isoDate,
    /** Bank transfer / voucher reference. */
    reference: optionalText(120),
})
    .strict();
/** `GET /payroll/runs/:id/bank-advice.xlsx?format=` — the generic list unless asked (ADR-073). */
export const bankAdviceQuerySchema = z
    .object({ format: z.enum(BANK_ADVICE_FORMATS).default('GENERIC') })
    .strict();
/** A company bank account number as the bank's upload file takes it: 10–20 digits. */
export const companyAccountNo = z
    .string()
    .trim()
    .regex(/^[0-9]{10,20}$/, 'Enter the account number: 10 to 20 digits');
/**
 * `PUT /payroll/bank-upload/:format` (payroll.settings, ADR-073): the bank of the lookup the
 * upload file pays into, and the company account it debits.
 */
export const bankUploadConfigUpdateSchema = z
    .object({ bankId: uuid, senderAccountNo: companyAccountNo })
    .strict();
export const bankUploadFormatParamSchema = z.object({ format: z.enum(BANK_UPLOAD_FORMATS) });
/** `GET /payroll/bank-upload` — every upload format and its settings (`null` until set). */
export const bankUploadConfigsSchema = z.object({
    items: z.array(z.object({
        format: z.enum(BANK_UPLOAD_FORMATS),
        label: z.string(),
        config: z
            .object({
            bankId: uuid,
            bankName: z.string(),
            senderAccountNo: z.string(),
            updatedAt: z.string(),
        })
            .nullable(),
    })),
});
// ── Settlement (FR-PAY-14, ADR-070) ──────────────────────────────────────────────────────────
/**
 * `GET /payroll/settlements/candidates` — leavers with an exit whose settlement is not settled
 * (Pending) and who are in no non-cancelled settlement run. No pay figures.
 */
export const settlementCandidateSchema = z.object({
    employeeId: uuid,
    empCode: z.number().int(),
    fullName: z.string(),
    lastWorkingDay: isoDate,
});
export const settlementCandidatesResponseSchema = z.object({
    items: z.array(settlementCandidateSchema),
});
// ── Arrears (FR-PAY-15, ADR-054, ADR-071) ────────────────────────────────────────────────────
/** A calendar month as `YYYY-MM`. */
export const yearMonth = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Use YYYY-MM');
/** Most months one arrear calculation may cover. */
export const MAX_ARREAR_MONTHS = 24;
const monthIndex = (ym) => Number(ym.slice(0, 4)) * 12 + Number(ym.slice(5, 7)) - 1;
/**
 * `POST /payroll/runs/:id/arrears/preview` — one employee of the run and the retroactive months
 * (inclusive) to re-price. The months must end before the run's month (checked by the API).
 */
/** The months must run forwards and span at most `MAX_ARREAR_MONTHS`. */
function checkArrearSpan(value, ctx) {
    const span = monthIndex(value.toMonth) - monthIndex(value.fromMonth) + 1;
    if (span < 1) {
        ctx.addIssue({ code: 'custom', path: ['toMonth'], message: 'Must not be before the start' });
    }
    else if (span > MAX_ARREAR_MONTHS) {
        ctx.addIssue({
            code: 'custom',
            path: ['toMonth'],
            message: `At most ${String(MAX_ARREAR_MONTHS)} months`,
        });
    }
}
export const arrearPreviewRequestSchema = z
    .object({ employeeId: uuid, fromMonth: yearMonth, toMonth: yearMonth })
    .strict()
    .superRefine(checkArrearSpan);
/**
 * The arrear calculation (FR-PAY-15, ADR-071): the salary on the 1st of the run's month, each
 * month of the range paid by an approved monthly run re-priced with it (frozen days and LWP),
 * months without one, and the total of the differences. `canAccept` is false when the total
 * is not positive (a decrease is never added as an arrear).
 */
export const arrearCalculationSchema = z.object({
    employeeId: uuid,
    salary: z.object({ gross: z.number().int(), effectiveDate: isoDate }),
    months: z.array(z.object({
        month: yearMonth,
        runId: uuid,
        runName: z.string(),
        paid: z.number().int(),
        repriced: z.number().int(),
        difference: z.number().int(),
    })),
    /** Months in the range with no approved monthly run paying this employee: ignored. */
    skipped: z.array(yearMonth),
    total: z.number().int(),
    canAccept: z.boolean(),
});
/**
 * `POST /payroll/runs/:id/arrears` — adds the arrear to the employee's line as a MANUAL `ARREAR`
 * input. The calculation is redone by the API; `amount` may differ from it (HR's edit) only with
 * a reason.
 */
export const arrearAcceptSchema = z
    .object({
    employeeId: uuid,
    fromMonth: yearMonth,
    toMonth: yearMonth,
    amount: money.min(1, 'Enter an amount above 0'),
    reason: optionalText(500),
})
    .strict()
    .superRefine(checkArrearSpan);
// ── Reports (FR-PAY-19, ADR-072) ─────────────────────────────────────────────────────────────
/**
 * The income year (Jul–Jun) a date falls in, by its starting year: 2026-03-15 → 2025
 * (Jul 2025 – Jun 2026).
 */
export function incomeYearOf(iso) {
    const year = Number(iso.slice(0, 4));
    return Number(iso.slice(5, 7)) >= 7 ? year : year - 1;
}
/** `2025` → `2025-26`. */
export function incomeYearLabel(year) {
    return `${String(year)}-${String((year + 1) % 100).padStart(2, '0')}`;
}
/** The twelve months (`YYYY-MM`) of an income year, July first. */
export function incomeYearMonths(year) {
    return Array.from({ length: 12 }, (_, i) => {
        const m = ((6 + i) % 12) + 1;
        return `${String(i < 6 ? year : year + 1)}-${String(m).padStart(2, '0')}`;
    });
}
/**
 * `GET /payroll/reports/ait` — an income year by its starting year (Jul `year` – Jun
 * `year + 1`), optionally for one employee. Without `payroll.tax.manage` the statement is
 * always the caller's own.
 */
export const aitStatementQuerySchema = z
    .object({
    year: z.coerce.number().int().min(2000).max(2099),
    employeeId: uuid.optional(),
})
    .strict();
/** Most months one PF report may cover. */
export const MAX_PF_REPORT_MONTHS = 24;
/** `GET /payroll/reports/pf` — pay months `from`…`to` (inclusive, `YYYY-MM`). */
export const pfReportQuerySchema = z
    .object({ from: yearMonth, to: yearMonth })
    .strict()
    .superRefine((value, ctx) => {
    const span = monthIndex(value.to) - monthIndex(value.from) + 1;
    if (span < 1) {
        ctx.addIssue({ code: 'custom', path: ['to'], message: 'Must not be before the start' });
    }
    else if (span > MAX_PF_REPORT_MONTHS) {
        ctx.addIssue({
            code: 'custom',
            path: ['to'],
            message: `At most ${String(MAX_PF_REPORT_MONTHS)} months`,
        });
    }
});
//# sourceMappingURL=payroll.js.map